import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Loader2 } from 'lucide-react';
import { adminAPI } from '../services/api';

const AdminMarketingNotificationPage: React.FC = () => {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [targetAudience, setTargetAudience] = useState<'customers' | 'drivers' | 'admins' | 'all' | 'anonymous_android' | 'anonymous_ios' | 'anonymous_all'>('all');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!title.trim() || !message.trim()) {
      alert('Please fill in both title and message');
      return;
    }

    const getAudienceLabel = (audience: string) => {
      switch (audience) {
        case 'all': return 'all users';
        case 'customers': return 'customers';
        case 'drivers': return 'drivers';
        case 'admins': return 'admins';
        case 'anonymous_android': return 'anonymous Android devices';
        case 'anonymous_ios': return 'anonymous iOS devices';
        case 'anonymous_all': return 'all anonymous devices';
        default: return 'users';
      }
    };
    const audienceLabel = getAudienceLabel(targetAudience);
    if (!window.confirm(`Send this notification to ${audienceLabel}?`)) {
      return;
    }

    try {
      setSending(true);
      const response = await adminAPI.sendMarketingNotification({
        title: title.trim(),
        message: message.trim(),
        targetAudience,
      });

      if (response.success) {
        const countLabel = targetAudience.startsWith('anonymous') ? 'devices' : 'users';
        alert(`Notification sent to ${response.data.successCount} ${countLabel}`);
        setTitle('');
        setMessage('');
        setTargetAudience('all');
      } else {
        alert(response.message || 'Failed to send notification');
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="bg-white border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/admin')}
              className="mr-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-gray-300" />
            </button>
            <h1 className="text-2xl font-bold text-white">Marketing Notification</h1>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="space-y-6">
            {/* Target Audience */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Target Audience
              </label>
              <div className="space-y-4">
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2">Logged-in Users</p>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setTargetAudience('all')}
                      className={`px-4 py-3 rounded-lg border-2 font-semibold transition-all ${
                        targetAudience === 'all'
                          ? 'border-black bg-white text-black'
                          : 'border-gray-200 text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      All Users
                    </button>
                    <button
                      onClick={() => setTargetAudience('customers')}
                      className={`px-4 py-3 rounded-lg border-2 font-semibold transition-all ${
                        targetAudience === 'customers'
                          ? 'border-black bg-white text-black'
                          : 'border-gray-200 text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Customers
                    </button>
                    <button
                      onClick={() => setTargetAudience('drivers')}
                      className={`px-4 py-3 rounded-lg border-2 font-semibold transition-all ${
                        targetAudience === 'drivers'
                          ? 'border-black bg-white text-black'
                          : 'border-gray-200 text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Drivers
                    </button>
                    <button
                      onClick={() => setTargetAudience('admins')}
                      className={`px-4 py-3 rounded-lg border-2 font-semibold transition-all ${
                        targetAudience === 'admins'
                          ? 'border-black bg-white text-black'
                          : 'border-gray-200 text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Admins
                    </button>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2">Not Logged In (Mobile Only)</p>
                  <div className="grid grid-cols-3 gap-3">
                    <button
                      onClick={() => setTargetAudience('anonymous_all')}
                      className={`px-4 py-3 rounded-lg border-2 font-semibold transition-all text-sm ${
                        targetAudience === 'anonymous_all'
                          ? 'border-green-600 bg-green-50 text-green-600'
                          : 'border-gray-200 text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      All Devices
                    </button>
                    <button
                      onClick={() => setTargetAudience('anonymous_android')}
                      className={`px-4 py-3 rounded-lg border-2 font-semibold transition-all text-sm ${
                        targetAudience === 'anonymous_android'
                          ? 'border-green-600 bg-green-50 text-green-600'
                          : 'border-gray-200 text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Android
                    </button>
                    <button
                      onClick={() => setTargetAudience('anonymous_ios')}
                      className={`px-4 py-3 rounded-lg border-2 font-semibold transition-all text-sm ${
                        targetAudience === 'anonymous_ios'
                          ? 'border-green-600 bg-green-50 text-green-600'
                          : 'border-gray-200 text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      iOS
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title *
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter notification title"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-black"
                maxLength={100}
              />
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message *
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Enter notification message"
                rows={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-black resize-none"
                maxLength={500}
              />
              <p className="mt-1 text-sm text-gray-500">{message.length}/500 characters</p>
            </div>

            {/* Send Button */}
            <button
              onClick={handleSend}
              disabled={sending || !title.trim() || !message.trim()}
              className={`w-full flex items-center justify-center px-6 py-3 rounded-lg font-semibold text-white transition-colors ${
                sending || !title.trim() || !message.trim()
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-black hover:bg-gray-800'
              }`}
            >
              {sending ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-5 w-5 mr-2" />
                  Send Notification
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminMarketingNotificationPage;

